import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = [
    "hiddenField",
    "previewText",
    "enabledCheckbox",
    "fields",
    "visualizationType",
    "regionFillFields",
    "markerFields",
    "mapSource",
    "mappingStrategy",
    "mappingField",
    "markerStyle",
    "markerCoordinates",
    "completedColor",
    "incompleteColor",
    "generateButton",
    "generateStatus"
  ]

  static values = { generateUrl: String }

  connect() {
    // Initialize with existing value if present
    if (this.hiddenFieldTarget.value) {
      try {
        const config = JSON.parse(this.hiddenFieldTarget.value)
        this.populateFields(config)
        this.updatePreview(config)
      } catch (e) {
        console.error('Invalid initial visualization config', e)
      }
    }
  }

  populateFields(config) {
    if (!config || config.type === 'none') {
      this.enabledCheckboxTarget.checked = false
      this.fieldsTarget.style.display = 'none'
      return
    }

    // Enable checkbox and show fields
    this.enabledCheckboxTarget.checked = true
    this.fieldsTarget.style.display = 'block'

    // Set visualization type
    const type = config.type || 'region-fill'
    this.visualizationTypeTargets.forEach(radio => {
      if (radio.value === type) {
        radio.checked = true
      }
    })

    // Show appropriate fields
    this.toggleTypeFieldsWithoutUpdate(type)

    // Populate common fields
    this.mapSourceTarget.value = config.map_source || ''

    // Populate region-fill fields
    if (type === 'region-fill') {
      this.mappingStrategyTarget.value = config.mapping_strategy || ''
      this.mappingFieldTarget.value = config.mapping_field || ''
    }

    // Populate marker fields
    if (type === 'marker') {
      this.markerStyleTarget.value = config.marker_style || 'circle'
      if (config.marker_coordinates) {
        this.markerCoordinatesTarget.value = JSON.stringify(config.marker_coordinates, null, 2)
      }
    }

    // Populate style colors
    if (config.style) {
      if (config.style.completed_color) {
        this.completedColorTarget.value = config.style.completed_color
      }
      if (config.style.incomplete_color) {
        this.incompleteColorTarget.value = config.style.incomplete_color
      }
    }
  }

  toggleFields(event) {
    const enabled = this.enabledCheckboxTarget.checked
    this.fieldsTarget.style.display = enabled ? 'block' : 'none'

    if (!enabled) {
      // Clear the config
      this.hiddenFieldTarget.value = ''
      this.updatePreview(null)
    } else {
      // Select default type if none selected
      if (!this.getSelectedType()) {
        this.visualizationTypeTargets[0].checked = true
        this.toggleTypeFields()
      }
      // Build initial config
      this.updateConfig()
    }
  }

  toggleTypeFields(event) {
    const type = this.getSelectedType()
    this.toggleTypeFieldsWithoutUpdate(type)
    this.updateConfig()
  }

  toggleTypeFieldsWithoutUpdate(type) {
    if (type === 'region-fill') {
      this.regionFillFieldsTarget.style.display = 'block'
      this.markerFieldsTarget.style.display = 'none'
    } else if (type === 'marker') {
      this.regionFillFieldsTarget.style.display = 'none'
      this.markerFieldsTarget.style.display = 'block'
    }
  }

  getSelectedType() {
    const selectedRadio = this.visualizationTypeTargets.find(radio => radio.checked)
    return selectedRadio ? selectedRadio.value : null
  }

  updateConfig() {
    if (!this.enabledCheckboxTarget.checked) {
      this.hiddenFieldTarget.value = ''
      this.updatePreview(null)
      return
    }

    const type = this.getSelectedType()
    if (!type) return

    const config = {
      type: type,
      map_source: this.mapSourceTarget.value
    }

    // Add type-specific fields
    if (type === 'region-fill') {
      if (this.mappingStrategyTarget.value) {
        config.mapping_strategy = this.mappingStrategyTarget.value
      }
      if (this.mappingFieldTarget.value) {
        config.mapping_field = this.mappingFieldTarget.value
      }
    } else if (type === 'marker') {
      if (this.markerStyleTarget.value) {
        config.marker_style = this.markerStyleTarget.value
      }

      // Parse marker coordinates JSON
      if (this.markerCoordinatesTarget.value.trim()) {
        try {
          const coords = JSON.parse(this.markerCoordinatesTarget.value)
          config.marker_coordinates = coords
        } catch (e) {
          console.error('Invalid marker coordinates JSON:', e)
          // Don't add invalid JSON to config
        }
      }
    }

    // Add style colors if set and different from defaults
    const completedColor = this.completedColorTarget.value
    const incompleteColor = this.incompleteColorTarget.value

    if (completedColor !== '#FFD700' || incompleteColor !== '#E8E8E8') {
      config.style = {}
      if (completedColor && completedColor !== '#FFD700') {
        config.style.completed_color = completedColor
      }
      if (incompleteColor && incompleteColor !== '#E8E8E8') {
        config.style.incomplete_color = incompleteColor
      }
    }

    this.hiddenFieldTarget.value = JSON.stringify(config)
    this.updatePreview(config)
  }

  async generateMarkerCoordinates() {
    const select = document.querySelector('[data-list-type-target="select"]')
    const listId = select ? select.value : null
    const listType = select && select.selectedOptions[0]
      ? select.selectedOptions[0].dataset.type
      : null

    if (!listId) {
      this.generateStatusTarget.textContent = 'Select a list first.'
      return
    }
    if (listType !== 'GoalSystem::StaticList') {
      this.generateStatusTarget.textContent = 'Generate is only available for static lists.'
      return
    }

    this.generateButtonTarget.disabled = true
    this.generateStatusTarget.textContent = 'Generating…'

    try {
      const url = `${this.generateUrlValue}?list_id=${encodeURIComponent(listId)}`
      const response = await fetch(url, { headers: { 'Accept': 'application/json' } })
      if (!response.ok) {
        let message = 'Could not generate coordinates.'
        try { const errorData = await response.json(); message = errorData.error || message } catch (_) { /* non-JSON error body */ }
        this.generateStatusTarget.textContent = message
        return
      }
      const data = await response.json()

      this.markerCoordinatesTarget.value = JSON.stringify(data.coordinates, null, 2)
      this.updateConfig()

      const parts = [`Generated ${data.included} of ${data.total} markers.`]
      if (data.skipped && data.skipped.length) {
        parts.push(`Skipped (no coordinates): ${data.skipped.join(', ')}.`)
      }
      if (data.duplicates && data.duplicates.length) {
        parts.push(`Duplicate names overwritten: ${data.duplicates.join(', ')}.`)
      }
      this.generateStatusTarget.textContent = parts.join(' ')
    } catch (e) {
      console.error('Marker coordinate generation failed', e)
      this.generateStatusTarget.textContent = 'Could not generate coordinates.'
    } finally {
      this.generateButtonTarget.disabled = false
    }
  }

  updatePreview(config) {
    if (!config) {
      this.previewTextTarget.textContent = 'None'
    } else if (config.type === 'region-fill') {
      const parts = [`Region Fill: ${config.map_source}`]
      if (config.mapping_strategy) {
        parts.push(`Strategy: ${config.mapping_strategy}`)
      }
      if (config.mapping_field) {
        parts.push(`Field: ${config.mapping_field}`)
      }
      this.previewTextTarget.textContent = parts.join(' | ')
    } else if (config.type === 'marker') {
      const markerCount = config.marker_coordinates ? Object.keys(config.marker_coordinates).length : 0
      const parts = [
        `Marker: ${config.map_source}`,
        `Style: ${config.marker_style || 'circle'}`,
        `${markerCount} marker${markerCount !== 1 ? 's' : ''}`
      ]
      this.previewTextTarget.textContent = parts.join(' | ')
    } else {
      this.previewTextTarget.textContent = 'None'
    }
  }
}
